import React, { useCallback, useState } from 'react'
import styled from '@emotion/styled'
import { Button, spacing, Text } from '@retailer-platform/shared-components'
import { type Rule } from '../RegexForm'
import { useDomainMessages } from '../../../../utils/domain/intl'
import { RegexCardRulesGridRow } from './RegexCardRulesGridRow'

interface Props {
  rules: Rule[]
  onChange: (rules: Rule[]) => void
  readOnly: boolean
}

const Grid = styled.div({
  display: 'grid',
  gridTemplateColumns: 'auto minmax(auto, 1fr) minmax(300px, auto) auto',
  columnGap: spacing.X12,
  rowGap: spacing.X8,
  padding: spacing.X24,
})
const GridContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: spacing.X8,
})

export const RegexCardRulesGrid = ({ rules, onChange, readOnly }: Props) => {
  const i18n = useDomainMessages({
    addRule: 'catalogAdminDomain.attributeManagement.regex.form.mutation.addRule',
    reorderRule: 'catalogAdminDomain.attributeManagement.regex.form.mutation.reorderRule',
    match: 'catalogAdminDomain.attributeManagement.regex.form.mutation.match',
    replace: 'catalogAdminDomain.attributeManagement.regex.form.mutation.replace',
    ruleActions: 'catalogAdminDomain.attributeManagement.regex.form.mutation.ruleActions',
  })
  const onRuleChange = useCallback(
    (rule: Rule, index: number) => {
      onChange(rules.map((r, i) => (i === index ? rule : r)))
    },
    [onChange, rules]
  )
  const onDelete = useCallback(
    (index: number) => {
      onChange(rules.filter((_, i) => i !== index))
    },
    [onChange, rules]
  )
  const onOrderChange = useCallback(
    (index: number, order: number) => {
      const rule = rules[index]
      rules[index] = rules[index + order]
      rules[index + order] = rule
      onChange(rules)
    },
    [onChange, rules]
  )
  const [addingRule, setAddingRule] = useState(false)
  return (
    <GridContainer>
      <Grid>
        <Text weight="bold" size="small">
          {i18n.reorderRule}
        </Text>
        <Text weight="bold" size="small">
          {i18n.match}
        </Text>
        <Text weight="bold" size="small">
          {i18n.replace}
        </Text>
        <Text weight="bold" size="small">
          {i18n.ruleActions}
        </Text>
        {rules.map((rule, index) => (
          <RegexCardRulesGridRow
            key={rule.match}
            rule={rule}
            onChange={r => onRuleChange(r, index)}
            onDelete={() => onDelete(index)}
            isNew={false}
            bannedMatches={rules.filter((_, i) => i !== index).map(r => r.match)}
            readOnly={readOnly}
            onOrderChange={o => onOrderChange(index, o)}
            disableNext={index === rules.length - 1}
            disablePrevious={index === 0}
          />
        ))}
        {addingRule && (
          <RegexCardRulesGridRow
            rule={{ match: '', replace: '', isDefault: false }}
            onChange={r => {
              onChange([...rules, r])
              setAddingRule(false)
            }}
            onDelete={() => setAddingRule(false)}
            isNew={true}
            bannedMatches={rules.map(r => r.match)}
            readOnly={readOnly}
            onOrderChange={() => {}}
            disableNext
            disablePrevious
          />
        )}
      </Grid>
      {!addingRule && !readOnly && (
        <Button
          variant="primary"
          onClick={() => setAddingRule(true)}
          style={{ alignSelf: 'center' }}
        >
          {i18n.addRule}
        </Button>
      )}
    </GridContainer>
  )
}
